import { normalizeCountryCode } from "./phone";

const MAX_SHARED_INVOICES = 5;

const digitsOnly = (v) => String(v ?? "").replace(/\D/g, "");

const money = (v, currency) => {
  const n = Number(v);
  const amount = Number.isNaN(n)
    ? "—"
    : n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${amount}` : amount;
};

const shortDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const invoiceIsOpen = (inv) => {
  const status = String(inv?.Status || "");
  return (Number(inv?.Balance_Amount) || 0) > 0.001 && status !== "Cancelled" && status !== "Paid";
};

/** Contact + issuer for a client, taken from its invoices (newest first). */
export function clientShareContext(invoices = [], clientLabel = "") {
  const withEmail = invoices.find((inv) => String(inv.Customer_Email || "").includes("@"));
  const withPhone = invoices.find((inv) => digitsOnly(inv.Customer_Phone));
  const withIssuer = invoices.find((inv) => inv.Issuer_Company_Name || inv.Issuer_Accounts_Email);
  return {
    name: String(invoices[0]?.Customer_Name || clientLabel || "Customer").trim(),
    email: withEmail ? String(withEmail.Customer_Email).trim() : "",
    phone: withPhone ? String(withPhone.Customer_Phone).trim() : "",
    countryCode: withPhone?.Customer_Country_Code || "",
    companyName: String(withIssuer?.Issuer_Company_Name || "Accounts").trim(),
    accountsEmail: String(withIssuer?.Issuer_Accounts_Email || "").trim(),
  };
}

const invoiceLine = (inv) => {
  const currency = String(inv.Currency_Code || "").trim();
  const due = invoiceIsOpen(inv) ? `due ${money(inv.Balance_Amount, currency)}` : String(inv.Status || "Paid");
  return `• ${inv.Invoice_No || "—"} (${shortDate(inv.Invoice_Date)}) — ${money(inv.Total_Amount, currency)}, ${due}`;
};

const outstandingTotal = (invoices) => {
  const open = invoices.filter(invoiceIsOpen);
  const currency = String(open[0]?.Currency_Code || "").trim();
  return money(open.reduce((sum, inv) => sum + (Number(inv.Balance_Amount) || 0), 0), currency);
};

const signature = (ctx) =>
  ctx.accountsEmail ? ["Regards,", ctx.companyName, `Accounts: ${ctx.accountsEmail}`] : ["Regards,", ctx.companyName];

/** Recent invoices summary for the client. */
export function buildInvoiceShareMessage(invoices, ctx) {
  const recent = invoices.filter((inv) => String(inv.Status || "") !== "Cancelled").slice(0, MAX_SHARED_INVOICES);
  const lines = [`Dear ${ctx.name},`, "", `Please find your invoice details from ${ctx.companyName}:`, ""];
  recent.forEach((inv) => lines.push(invoiceLine(inv)));
  if (invoices.some(invoiceIsOpen)) lines.push("", `Total outstanding: ${outstandingTotal(invoices)}`);
  lines.push("", ...signature(ctx));
  return lines.join("\n");
}

/** Payment reminder covering every open invoice. */
export function buildReminderMessage(invoices, ctx) {
  const open = invoices.filter(invoiceIsOpen);
  const lines = [
    `Dear ${ctx.name},`,
    "",
    `This is a friendly reminder from ${ctx.companyName} that the following invoice${open.length === 1 ? " has" : "s have"} an outstanding balance:`,
    "",
  ];
  open.forEach((inv) => {
    const dueDate = inv.Due_Date ? `, due by ${shortDate(inv.Due_Date)}` : "";
    lines.push(`• ${inv.Invoice_No || "—"} (${shortDate(inv.Invoice_Date)}) — ${money(inv.Balance_Amount, inv.Currency_Code)}${dueDate}`);
  });
  lines.push("", `Total due: ${outstandingTotal(invoices)}`, "", "Please arrange payment at your earliest convenience.", "");
  lines.push(...signature(ctx));
  return lines.join("\n");
}

export function whatsappHref(ctx, text) {
  const raw = String(ctx.phone || "").trim();
  const digits = digitsOnly(raw);
  if (!digits) return null;
  const e164 = raw.startsWith("+") || digits.length > 10 ? digits : `${digitsOnly(normalizeCountryCode(ctx.countryCode))}${digits}`;
  return `https://wa.me/${e164}?text=${encodeURIComponent(text)}`;
}

export function emailHref(ctx, subject, body) {
  if (!ctx.email) return null;
  const fullSubject = ctx.companyName && ctx.companyName !== "Accounts" ? `[${ctx.companyName}] ${subject}` : subject;
  const params = [`subject=${encodeURIComponent(fullSubject)}`, `body=${encodeURIComponent(body)}`];
  if (ctx.accountsEmail && ctx.accountsEmail.toLowerCase() !== ctx.email.toLowerCase()) {
    params.push(`bcc=${encodeURIComponent(ctx.accountsEmail)}`);
  }
  return `mailto:${ctx.email}?${params.join("&")}`;
}
